import { useState } from 'react'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/components/ui/dialog'
import { Button } from '@/shared/components/ui/button'
import api from '@/admin/lib/api'
import type { ApiKey } from '@/admin/types/api-keys'

import { toastApiError } from '@/shared/lib/api-error'
interface RevokeApiKeyDialogProps {
  open: boolean
  onClose: () => void
  apiKey: ApiKey | null
  onRevoked: () => void
}

export function RevokeApiKeyDialog({
  open,
  onClose,
  apiKey,
  onRevoked,
}: RevokeApiKeyDialogProps) {
  const [revoking, setRevoking] = useState(false)

  async function handleRevoke() {
    if (!apiKey) return
    setRevoking(true)
    try {
      await api.post(`/auth/api-keys/${apiKey.uuid}/revoke/`)
      toast.success('API key revoked')
      onRevoked()
      onClose()
    } catch (err) {
      toastApiError(err, 'Failed to revoke API key');
    } finally {
      setRevoking(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(value) => { if (!value) onClose() }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Revoke API Key</DialogTitle>
        </DialogHeader>

        <p className="text-[13px] text-[var(--ink-2)]">
          Are you sure you want to revoke{' '}
          <span className="font-medium text-[var(--ink)]">{apiKey?.name}</span>?
          This action cannot be undone. Any systems using this key will lose access immediately.
        </p>

        <DialogFooter className="border-t-[0.5px] border-t-[var(--rule)] pt-3">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={handleRevoke} disabled={revoking}>
            {revoking && <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />}
            Revoke
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
